/**
 * /customer/promotions/:id — Promotion Detail (Phase W-2).
 *
 * Web counterpart of the mobile detail screen:
 *   - hero (full-width cover)
 *   - body card (brand link, optional EXPIRED pill, title, validity,
 *     description, long_description, T&Cs, branch list)
 *
 * Plain-text rendering with preserved whitespace via Tailwind
 * `whitespace-pre-wrap` (no new markdown lib).
 *
 * Backward compat — when long_description / terms_conditions are
 * empty strings (legacy promos predate W-2), the corresponding
 * section is omitted rather than rendering a hollow heading.
 */
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, MapPin, AlertCircle, Tag, Calendar,
} from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

function fmt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function buildValidityLine(p) {
  const start = fmt(p.validity_start);
  const end = fmt(p.valid_until);
  if (!end && !start) return null;
  if (start && end) {
    return p.is_expired
      ? `Ran from ${start} to ${end}`
      : `Valid from ${start} to ${end}`;
  }
  if (end) return p.is_expired ? `Ended ${end}` : `Valid until ${end}`;
  return null;
}

export default function CustomerPromotionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);

  const promoQ = useQuery({
    queryKey: qk.customerPromotionDetail(id),
    queryFn: () => api.customerPromotionDetail(token, id),
    enabled: !!token && !!id,
    retry: (failureCount, err) => {
      // Don't retry on 404 — promo is soft-deleted or brand not visible.
      if (err?.status === 404) return false;
      return failureCount < 2;
    },
  });

  if (promoQ.isLoading) {
    return (
      <div className="space-y-6" data-testid="customer-promotion-detail-loading">
        <div className="h-72 rounded-3xl bg-white/[0.04] animate-pulse" />
        <div className="h-48 rounded-3xl bg-white/[0.03] animate-pulse" />
      </div>
    );
  }

  if (promoQ.error) {
    const is404 = promoQ.error?.status === 404;
    return (
      <div
        className="max-w-md mx-auto text-center py-16"
        data-testid="customer-promotion-detail-error"
      >
        <AlertCircle size={28} className="mx-auto text-white/40 mb-3" />
        <div className="font-display text-lg font-semibold">
          {is404
            ? 'This promotion is no longer available'
            : 'Could not load promotion'}
        </div>
        <p className="text-sm text-white/55 mt-2">
          {is404
            ? 'It may have ended or been removed by the brand.'
            : 'Please try again in a moment.'}
        </p>
        <button
          onClick={() => navigate('/customer/promotions')}
          className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1A1A1A] border border-white/12 hover:border-gold/40 text-sm font-medium"
          data-testid="customer-promotion-detail-back"
        >
          <ChevronLeft size={14} /> All promotions
        </button>
      </div>
    );
  }

  const promo = promoQ.data;
  const validity = buildValidityLine(promo);
  const longBody = (promo.long_description ?? '').trim();
  const tcBody = (promo.terms_conditions ?? '').trim();
  const branches = promo.branches ?? [];

  return (
    <div className="space-y-6" data-testid="customer-promotion-detail-screen">
      {/* Hero */}
      <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#15151B]">
        <div className="aspect-[16/7] md:aspect-[21/9] relative">
          {promo.image_url ? (
            <img
              src={resolveImageUrl(promo.image_url)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
          {/* back chip */}
          <button
            onClick={() => navigate(-1)}
            data-testid="customer-promotion-detail-back-hero"
            aria-label="Go back"
            className="absolute top-4 left-4 w-10 h-10 grid place-items-center rounded-full bg-black/55 border border-white/15 backdrop-blur text-white hover:bg-black/70"
          >
            <ChevronLeft size={18} />
          </button>
        </div>
      </div>

      {/* Body card */}
      <div className="rounded-3xl border border-white/10 bg-[#15151B] p-6 md:p-8 space-y-7">
        {/* Brand link */}
        <Link
          to={`/customer/brand/${promo.brand_id}`}
          data-testid="customer-promo-detail-brand-link"
          className="flex items-center gap-3 group"
        >
          {promo.brand_logo_url ? (
            <img
              src={resolveImageUrl(promo.brand_logo_url)}
              alt=""
              className="w-10 h-10 rounded-xl border border-white/10"
            />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-gold/15 border border-gold/30 grid place-items-center text-gold font-semibold">
              {(promo.brand_name ?? '?').slice(0, 1)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gold truncate">
              {promo.brand_name}
            </div>
            <div className="text-xs text-white/45 mt-0.5">View brand</div>
          </div>
          <ChevronRight
            size={16}
            className="text-white/35 group-hover:text-white/65 transition-colors"
          />
        </Link>

        {/* Title block */}
        <div className="space-y-3">
          {promo.is_expired ? (
            <div
              className="inline-flex items-center px-2.5 py-1 rounded-full bg-rose-500/15 border border-rose-400/55 text-[10px] font-mono uppercase tracking-[0.18em] text-rose-300"
              data-testid="customer-promo-detail-expired-badge"
            >
              Expired
            </div>
          ) : null}
          <h1 className="font-display text-2xl md:text-3xl font-bold leading-tight">
            {promo.title}
          </h1>
          {validity ? (
            <div
              className="inline-flex items-center gap-1.5 text-sm text-white/65"
              data-testid="customer-promo-detail-validity"
            >
              <Calendar size={13} className="text-gold" />
              {validity}
            </div>
          ) : null}
        </div>

        {/* Short description */}
        {promo.description ? (
          <p className="text-[15px] leading-relaxed text-white/90">
            {promo.description}
          </p>
        ) : null}

        {/* Long description (omit when empty for legacy promos) */}
        {longBody ? (
          <DetailSection label="About this promotion">
            <p className="text-sm leading-relaxed text-white/85 whitespace-pre-wrap">
              {longBody}
            </p>
          </DetailSection>
        ) : null}

        {/* Terms & conditions */}
        {tcBody ? (
          <DetailSection label="Terms & conditions">
            <p className="text-sm leading-relaxed text-white/80 whitespace-pre-wrap">
              {tcBody}
            </p>
          </DetailSection>
        ) : null}

        {/* Available at */}
        <DetailSection
          label="Available at"
          testID="customer-promo-detail-available-at"
        >
          {branches.length === 0 ? (
            <div className="inline-flex items-center gap-2 text-sm font-medium">
              <MapPin size={14} className="text-gold" />
              All branches
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {branches.map((b) => (
                <div
                  key={b.id}
                  className="inline-flex items-center gap-2 text-sm font-medium"
                  data-testid={`customer-promo-detail-branch-${b.id}`}
                >
                  <MapPin size={14} className="text-gold" />
                  {b.name}
                </div>
              ))}
            </div>
          )}
        </DetailSection>

        {/* footer chip — promo id for support diagnostics; tiny, low-contrast */}
        <div className="pt-2 border-t border-white/5 flex items-center gap-1.5 text-[10px] font-mono text-white/30">
          <Tag size={10} /> {promo.id.slice(0, 8)}
        </div>
      </div>
    </div>
  );
}

function DetailSection({ label, children, testID }) {
  return (
    <section className="space-y-2.5" data-testid={testID}>
      <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-gold">
        {label}
      </div>
      {children}
    </section>
  );
}
